// src/encounter/structured-note/risk-optional-block.tsx
//
// PROD-3404 (`?x=nurse-encounter-v3`; Antonin, 3 Nov: "Fall Assessment: check how this section is done" (Claude Design card 18);
// "Pain Assessment: works exactly like fall risk" (card 19)). Fall and Pain as the catalog's optional block (scrips-signal-ds
// feat/signal @62cb3650, `EncounterDev.tsx` `OptionalBlock`): one row with the question and its two chips, nothing picked at
// first. Yes opens the assessment's lines (the STEADI follow-up under Fall, the details under Pain); a chevron folds them back to
// a summary. No keeps the row folded and reads "No fall risk" / "No pain". A form section, not a nested card. The summary
// wraps (the catalog cuts it with an ellipsis; the note's ENC-L12 law cuts nothing).
//
// Display only. The "present" answer is the same item and the same write (the caller's Risk-bridge write); the lines under it
// are the caller's own rows. A line that already holds an answer while the question is not Yes (an old record's Morse history)
// still shows under the row, as before, so no recorded fact is hidden.
import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import type { QItem } from '@scripsteam/scrips-signal-ds';
import { ChipChoice } from './chip-choice';
import { optionalBlockWords } from './risk-optional-words';

export interface RiskOptionalBlockProps {
  /** The bridged group (Fall / Pain), for the row's accessible name. */
  group: QItem;
  /** The group's "present" boolean item. */
  present: QItem;
  /** Its stored answer: true, false or nothing yet. */
  value: boolean | undefined;
  readOnly: boolean;
  /** The caller's write of the present answer (the Risk bridge). */
  onPresentChange: (yes: boolean) => void;
  /** "label: value" of the answered lines under it, for the folded summary (the first two, then "+N"). */
  answered: readonly string[];
  /** The lines under the question, drawn while it is Yes and open. */
  children: ReactNode;
  /** Lines that hold an answer although the question is not Yes (history): drawn under the row, never hidden. */
  kept?: ReactNode;
}

export function RiskOptionalBlock({ group, present, value, readOnly, onPresentChange, answered, children, kept }: RiskOptionalBlockProps) {
  const words = optionalBlockWords(present.text);
  const on = value === true;
  const [open, setOpen] = useState(false);
  const summary =
    value === false ? words.noSummary : answered.slice(0, 2).join(' · ') + (answered.length > 2 ? ` · +${answered.length - 2}` : '');
  return (
    <div role="group" aria-label={group.text ?? words.question} className="flex min-w-0 flex-col gap-4" data-risk-optional data-open={(on && open) || undefined}>
      <div className="flex min-h-8 items-center gap-3" data-part="risk-optional-head">
        {on ? (
          <button
            type="button"
            className="inline-flex shrink-0 items-center gap-1 border-0 bg-transparent p-0 text-text-primary cursor-pointer"
            aria-expanded={open}
            // Its own name: after Yes the chip is named after the question too ("Pain"), and a screen reader must tell them apart.
            aria-label={`${open ? 'Hide' : 'Show'} ${words.question} details`}
            onClick={() => setOpen(!open)}
          >
            <ChevronDown size={16} aria-hidden className={`text-text-secondary transition-transform ${open ? '' : '-rotate-90'}`} />
            <span className="text-size-sm font-semibold">{words.question}</span>
          </button>
        ) : (
          <span className="shrink-0 text-size-sm font-semibold text-text-primary">{words.question}</span>
        )}
        {!open && summary ? (
          <span className="min-w-0 flex-1 break-words text-size-xs text-text-secondary" data-part="risk-optional-summary">
            {summary}
          </span>
        ) : null}
        <span className="ml-auto shrink-0">
          <ChipChoice
            options={[
              { value: 'false', label: words.no },
              { value: 'true', label: words.yes },
            ]}
            value={value === true ? 'true' : value === false ? 'false' : null}
            onChange={(next) => {
              if (next !== 'true' && next !== 'false') return;
              const yes = next === 'true';
              onPresentChange(yes);
              setOpen(yes);
            }}
            requiredLabel={present.required ? words.question : undefined}
            readOnly={readOnly}
            aria-label={words.question}
          />
        </span>
      </div>
      {on && open ? <div className="flex flex-col gap-1">{children}</div> : null}
      {!on && kept ? <div className="flex flex-col gap-1">{kept}</div> : null}
    </div>
  );
}
